import React from 'react'
import { withStyles } from '@mui/styles'
import {
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Tooltip,
  Checkbox,
  Typography,
  Box,
  Icon,
} from '@mui/material'
import { selectInputStyles } from '../select-input/select-input.styles'

/**
 * CnaeSelectInput
 * Un componente select específico para CNAE que muestra tooltips con la descripción de cada opción.
 *
 * @component
 */
const CnaeSelectInputBase = ({
  classes,
  values = [],
  multiple = false,
  readOnly = false,
  selectAll = false,
  ...props
}) => {
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'
  const value = React.useMemo(() => props.value || (multiple ? [] : ''), [props.value, multiple])

  const getValue = () => {
    if (multiple) {
      let optionsSelected = []
      value.forEach((v) => {
        const optionSelected = values.find((item) => item.key === v)
        if (optionSelected) optionsSelected.push(optionSelected.value)
      })
      return optionsSelected.join(', ')
    } else {
      let valueSelected = '--'
      if (value && values) {
        const optionSelected = values.find((option) => option.key === value)
        valueSelected = optionSelected ? optionSelected.value : '--'
      }
      return valueSelected
    }
  }

  const selectedDescription = React.useMemo(() => {
    if (!value || (multiple && value.length === 0)) return null
    if (multiple) {
      const selectedItems = values.filter((item) => value.indexOf(item.key) > -1)
      const descriptions = selectedItems
        .map((item) => item.descripcion || item.description)
        .filter((d) => !!d)
      return descriptions.length > 0 ? descriptions.join('\n\n') : null
    } else {
      const selected = values.find((item) => item.key === value)
      return selected ? selected.descripcion || selected.description : null
    }
  }, [value, values, multiple])

  const selectValues = () => {
    return values.map((item, i) => (
      <MenuItem key={item.key || i} value={item.key} disabled={item.disabled}>
        <Box width="100%" display="flex" alignItems="center">
          {multiple ? (
            <>
              <Checkbox checked={value.indexOf(item.key) > -1} />
              <ListItemText primary={item.value} />
            </>
          ) : (
            item.value
          )}
        </Box>
      </MenuItem>
    ))
  }

  const allSelected =
    multiple && values.length && value.length ? values.length === value.length : false

  const changeHandle = (e) => {
    if (selectAll && e.target.value.includes('all')) {
      return props.onChange({
        ...e,
        target: {
          ...e.target,
          value: allSelected ? [] : values.map((el) => el.key),
        },
      })
    }
    return props.onChange(e)
  }

  const renderValueText = (selected) => {
    let text = ''
    if (multiple) {
      text = selected
        .map((item) => {
          const found = values.find((value) => value.key === item)
          return found ? found.value : 'Unknown'
        })
        .join(', ')
    } else {
      const option = values.find((item) => item.key === selected)
      text = option ? option.value : selected
    }

    return (
      <Box
        component="div"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {text}
      </Box>
    )
  }

  return (
    <FormControl
      variant="outlined"
      className={classes.root}
      required={props.required}
      disabled={props.disabled}
      fullWidth
    >
      <Box display="flex" flexDirection="row" alignItems="center">
        {props.title && (
          <Typography variant={`subtitle${typographyVariant}`}>
            {props.title} {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
          </Typography>
        )}
        {props.tooltip && (
          <span style={{ fontSize: 16, marginLeft: 4 }}>
            <Tooltip
              placement="top"
              title={
                <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                  {props.tooltip}
                </Typography>
              }
            >
              <Icon fontSize="inherit">help_outline</Icon>
            </Tooltip>
          </span>
        )}
      </Box>
      <Box display="flex" alignItems="center" width="100%">
        <Box flexGrow={1} overflow="hidden" display="flex" alignItems="center" minWidth={0}>
          {readOnly ? (
            <Typography
              variant={`body${typographyVariant}`}
              color="textSecondary"
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {getValue()}
            </Typography>
          ) : (
            <Select
              fullWidth
              className={classes.input}
              name={props.name}
              value={value}
              multiple={multiple}
              onChange={changeHandle}
              renderValue={renderValueText}
              sx={{
                '& .MuiSelect-select': {
                  paddingRight: '32px !important',
                },
              }}
              MenuProps={{
                variant: 'menu',
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                sx: {
                  '& .MuiPaper-root': {
                    transformOrigin: 'left top !important',
                  },
                  '& .MuiList-root': {
                    textAlign: 'left',
                  },
                },
              }}
            >
              {selectAll && multiple ? (
                <MenuItem divider value="all">
                  <Checkbox checked={allSelected ? true : false}></Checkbox>
                  <ListItemText>Seleccionar todos</ListItemText>
                </MenuItem>
              ) : null}
              {selectValues()}
            </Select>
          )}
        </Box>
        {selectedDescription && (
          <Tooltip
            placement="right"
            title={
              <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                {selectedDescription}
              </Typography>
            }
          >
            <Icon fontSize="inherit" style={{ marginLeft: 8, cursor: 'pointer', color: '#000000' }}>
              help_outline
            </Icon>
          </Tooltip>
        )}
      </Box>
    </FormControl>
  )
}

export const CnaeSelectInput = React.memo(withStyles(selectInputStyles)(CnaeSelectInputBase))
